import { z } from 'zod';
export const UserRole = z.enum(['gast', 'ersteller', 'freigeber', 'admin']);
export const ProjectStatus = z.enum(['draft', 'planning', 'in_review', 'approved', 'completed']);
export const Priority = z.enum(['low', 'medium', 'high']);
export const BuildingClass = z.enum(['GKL 1', 'GKL 2', 'GKL 3', 'GKL 4', 'GKL 5']);
export const FireResistance = z.enum(['F30', 'F60', 'F90', 'F120', 'F180']);
export const BuildingType = z.enum([
    'Wohngebäude',
    'Bürogebäude',
    'Industriebau',
    'Versammlungsstätte',
    'Verkaufsstätte',
    'Schule',
    'Krankenhaus',
    'Hotel',
    'Garage',
    'Sonderbau',
    'Sonstiges',
]);
export const MainUse = z.enum([
    'Wohnen',
    'Gewerbe',
    'Verwaltung',
    'Industrie/Produktion',
    'Lagerung',
    'Versammlung',
    'Handel',
    'Bildung',
    'Gesundheit',
    'Beherbergung',
    'Sonstiges',
]);
export const WaterSupplyKind = z.enum(['Grundschutz', 'Objektschutz']);
export const FireDepartmentAccess = z.enum(['vorhanden', 'erforderlich', 'nicht_erforderlich']);
/**
 * Beschriftung der Feuerwehrzufahrt.
 *
 * Der Rohwert (`nicht_erforderlich`) taugt weder für das Formular noch für den
 * Nachweis. Die Map liegt deshalb hier und nicht im Frontend: dieselbe
 * Beschriftung muss im erzeugten Dokument stehen wie im Formular — zwei
 * getrennte Übersetzungen wären zwei Wahrheiten.
 */
export const FIRE_DEPARTMENT_ACCESS_LABEL = {
    vorhanden: 'Vorhanden',
    erforderlich: 'Erforderlich',
    nicht_erforderlich: 'Nicht erforderlich',
};
export const DocSuggestionType = z.enum([
    'existing_project',
    'existing_projects',
    'new_project_existing_customer',
    'new_customer',
    'manual_assignment',
    // Objektbetreuung: Das Dokument gehört zu einer Liegenschaft (und ggf. zu
    // einem Typ aus dem Dokumentkatalog), nicht zu einem Bauvorhaben.
    'existing_liegenschaft',
]);
export const SimilarityConfidence = z.enum(['low', 'medium', 'high']);
// ============================================================================
// Iteration 3 — Domain-Erweiterung (Bundesland + Sonderbau + Verfahren)
// ============================================================================
export const FederalState = z.enum([
    'BW',
    'BY',
    'BE',
    'BB',
    'HB',
    'HH',
    'HE',
    'MV',
    'NI',
    'NW',
    'RP',
    'SL',
    'SN',
    'ST',
    'SH',
    'TH',
]);
export const FEDERAL_STATE_LABEL = {
    BW: 'Baden-Württemberg',
    BY: 'Bayern',
    BE: 'Berlin',
    BB: 'Brandenburg',
    HB: 'Bremen',
    HH: 'Hamburg',
    HE: 'Hessen',
    MV: 'Mecklenburg-Vorpommern',
    NI: 'Niedersachsen',
    NW: 'Nordrhein-Westfalen',
    RP: 'Rheinland-Pfalz',
    SL: 'Saarland',
    SN: 'Sachsen',
    ST: 'Sachsen-Anhalt',
    SH: 'Schleswig-Holstein',
    TH: 'Thüringen',
};
export const ProjectKind = z.enum(['brandschutznachweis', 'brandschutzkonzept', 'stellungnahme']);
export const PROJECT_KIND_LABEL = {
    brandschutznachweis: 'Brandschutznachweis',
    brandschutzkonzept: 'Brandschutzkonzept',
    stellungnahme: 'Brandschutz-Stellungnahme',
};
export const BauVorhabenart = z.enum([
    'neubau',
    'umbau',
    'nutzungsaenderung',
    'erweiterung',
    'bestand',
]);
export const BAU_VORHABENART_LABEL = {
    neubau: 'Neubau',
    umbau: 'Umbau',
    nutzungsaenderung: 'Nutzungsänderung',
    erweiterung: 'Erweiterung',
    bestand: 'Bestand',
};
export const Bauweise = z.enum(['massivbau', 'holzbau', 'stahlbau', 'mischbau']);
export const BAUWEISE_LABEL = {
    massivbau: 'Massivbau',
    holzbau: 'Holzbau',
    stahlbau: 'Stahlbau',
    mischbau: 'Mischbau',
};
export const SonderbauTyp = z.enum([
    'versammlungsstaette_klein',
    'versammlungsstaette_gross',
    'verkaufsstaette',
    'industriebau',
    'hochhaus',
    'hochhaus_gross',
    'krankenhaus',
    'beherbergung',
    'schule',
    'kita',
    'buero_gross',
    'garage_klein',
    'garage_mittel',
    'garage_gross',
    'sonstiger_sonderbau',
]);
export const SONDERBAU_TYP_LABEL = {
    versammlungsstaette_klein: 'Versammlungsstätte > 200 Personen',
    versammlungsstaette_gross: 'Versammlungsstätte > 1.000 Personen',
    verkaufsstaette: 'Verkaufsstätte (> 2.000 m²)',
    industriebau: 'Industriebau (IndBauR/DIN 18230)',
    hochhaus: 'Hochhaus (> 22 m bis < 60 m)',
    hochhaus_gross: 'Hochhaus (≥ 60 m)',
    krankenhaus: 'Krankenhaus',
    beherbergung: 'Beherbergungsstätte (> 12 Betten)',
    schule: 'Schule',
    kita: 'Kindertagesstätte',
    buero_gross: 'Bürogebäude (> 400 m²)',
    garage_klein: 'Kleingarage (≤ 100 m²)',
    garage_mittel: 'Mittelgarage (100–1.000 m²)',
    garage_gross: 'Großgarage (> 1.000 m²)',
    sonstiger_sonderbau: 'Sonstiger Sonderbau',
};
export const Sicherheitskategorie = z.enum(['K1', 'K2', 'K3', 'K4']);
export const SICHERHEITSKATEGORIE_LABEL = {
    K1: 'K1 — ohne anlagentechnische Maßnahmen',
    K2: 'K2 — BMA',
    K3: 'K3 — BMA + Werkfeuerwehr',
    K4: 'K4 — autom. Löschanlage',
};
export const Lagerart = z.enum(['keine', 'niedrig', 'mittel', 'hoch', 'hochregal']);
export const LAGERART_LABEL = {
    keine: 'Keine Lagerung',
    niedrig: 'Niedrig',
    mittel: 'Mittel',
    hoch: 'Hoch',
    hochregal: 'Hochregallager',
};
export const BMAKategorie = z.enum(['kein', 'teilschutz', 'vollschutz', 'erweiterter_schutz']);
export const BMA_KATEGORIE_LABEL = {
    kein: 'Keine BMA',
    teilschutz: 'Teilschutz nach DIN 14675',
    vollschutz: 'Vollschutz nach DIN 14675',
    erweiterter_schutz: 'Erweiterter Schutz nach DIN 14675',
};
export const SprinklerKlasse = z.enum([
    'LH',
    'OH1',
    'OH2',
    'OH3',
    'OH4',
    'HHP_S1',
    'HHP_S2',
    'HHP_S3',
]);
export const Verfahrensart = z.enum([
    'genehmigungsfreistellung',
    'vereinfachtes_verfahren',
    'regulaeres_verfahren',
    'sonderbauverfahren',
]);
export const VERFAHRENSART_LABEL = {
    genehmigungsfreistellung: 'Genehmigungsfreistellung (Art. 58 BayBO bzw. Äquivalent)',
    vereinfachtes_verfahren: 'Vereinfachtes Verfahren (Art. 59 BayBO bzw. Äquivalent)',
    regulaeres_verfahren: 'Reguläres Baugenehmigungsverfahren (Art. 60 BayBO bzw. Äquivalent)',
    sonderbauverfahren: 'Sonderbauverfahren',
};
export const PruefWeg = z.enum([
    'bauaufsicht',
    'pruefsachverstaendige',
    'pruefingenieur',
    'bauherrenverantwortung',
]);
export const PRUEF_WEG_LABEL = {
    bauaufsicht: 'Bauaufsichtliche Prüfung (Behörde)',
    pruefsachverstaendige: 'Prüfsachverständige (PSV nach PrüfVBau)',
    pruefingenieur: 'Prüfingenieur Brandschutz (PfB)',
    bauherrenverantwortung: 'Bauherrenverantwortung (keine Prüfung)',
};
export const HochhausTyp = z.enum(['kein', 'hochhaus_klein', 'hochhaus_gross']);
/**
 * Beschriftungen der Auswahlfelder.
 *
 * Ohne sie zeigt das Formular den rohen Schlüssel („hochhaus_klein"). Bewusst
 * OHNE Zahlenangaben: Die Schwellenwerte sind bundeslandabhängig und stehen
 * versioniert in `data/state-rules.json` — sie hier zu wiederholen hiesse, sie
 * an zwei Stellen pflegen zu müssen.
 */
export const HOCHHAUS_TYP_LABEL = {
    kein: 'Kein Hochhaus',
    hochhaus_klein: 'Hochhaus',
    hochhaus_gross: 'Großhochhaus',
};
export const GarageTyp = z.enum(['klein', 'mittel', 'gross']);
export const GARAGE_TYP_LABEL = {
    klein: 'Kleingarage',
    mittel: 'Mittelgarage',
    gross: 'Großgarage',
};
export const BehoerdenvorgangPhase = z.enum([
    'einreichung',
    'pruefung',
    'maengel',
    'nachreichung',
    'freigabe',
    'abgelehnt',
]);
/**
 * Phasen, in denen ein Behördenvorgang erledigt ist.
 *
 * Ab hier läuft keine Frist mehr: Eine abgelehnte Einreichung, deren Frist seit
 * Monaten „überzogen" blinkt, ist schlicht falsch — und ein endgültig
 * abgelehnter Vorgang bleibt sonst für immer als kritisch markiert.
 * Eine Stelle, damit Erinnerungsjob und Oberfläche nicht auseinanderlaufen.
 */
export const BEHOERDENVORGANG_ERLEDIGTE_PHASEN = [
    'freigabe',
    'abgelehnt',
];
export function istVorgangErledigt(phase) {
    return BEHOERDENVORGANG_ERLEDIGTE_PHASEN.includes(phase);
}
export const BehoerdenvorgangKind = z.enum([
    'bauantrag',
    'brandschutznachweis_einreichung',
    'brandschutzkonzept_einreichung',
    'stellungnahme',
    'maengelschreiben',
    'nachreichung',
    'freigabe',
    'schlussabnahme',
    'korrespondenz',
]);
/**
 * Nutzungsart einer betreuten Liegenschaft.
 *
 * Bewusst NICHT `SonderbauTyp`: Der beantwortet die bauordnungsrechtliche
 * Frage „ist das ein Sonderbau und welcher" und gehört ans Bauvorhaben. Die
 * Nutzungsart beschreibt, was in dem Objekt tatsächlich passiert — sie ist die
 * Eingangsgröße, aus der sich Prüfpflichten und Gefährdungen ableiten, und sie
 * gilt auch für Objekte, die nie ein Bauvorhaben bei uns hatten.
 *
 * Eine Liegenschaft kann mehrere Nutzungen haben (Hotel mit Gaststätte und
 * Tiefgarage). Deshalb wird sie als Liste geführt, nicht als Einzelwert.
 */
export const Nutzungsart = z.enum([
    'buero',
    'verkauf',
    'produktion',
    'lager',
    'gastronomie',
    'beherbergung',
    'versammlungsstaette',
    'bildung',
    'betreuung_pflege',
    'gesundheit',
    'garage_parken',
    'wohnen',
    'landwirtschaft',
    'sonstiges',
]);
export const NUTZUNGSART_LABEL = {
    buero: 'Büro und Verwaltung',
    verkauf: 'Verkaufsstätte',
    produktion: 'Produktion und Handwerk',
    lager: 'Lager',
    gastronomie: 'Gastronomie',
    beherbergung: 'Beherbergung',
    versammlungsstaette: 'Versammlungsstätte',
    bildung: 'Schule und Bildung',
    betreuung_pflege: 'Betreuung und Pflege',
    gesundheit: 'Gesundheitswesen',
    garage_parken: 'Garage und Parken',
    wohnen: 'Wohnen',
    landwirtschaft: 'Land- und Forstwirtschaft',
    sonstiges: 'Sonstiges',
};
/**
 * Stand der Betreuung einer Liegenschaft.
 *
 * `interessent` ist bewusst dabei: Objekte werden regelmäßig erfasst, bevor der
 * Auftrag steht (Angebot, Begehungstermin). Sie dürfen dann in Stammdaten und
 * Routenplanung auftauchen, aber keine Fristen auslösen.
 */
export const BetreuungStatus = z.enum(['interessent', 'aktiv', 'ruhend', 'beendet']);
export const BETREUUNG_STATUS_LABEL = {
    interessent: 'Interessent',
    aktiv: 'Aktiv betreut',
    ruhend: 'Ruhend',
    beendet: 'Betreuung beendet',
};
/**
 * Art einer brandschutztechnischen Anlage oder eines Bauteils.
 *
 * Der Katalog folgt dem, was ein Betreuungsbetrieb tatsächlich prüft und
 * wartet — er ist kein Versuch, die gesamte Brandschutztechnik abzubilden.
 * Die Art entscheidet, welche Prüfzyklen greifen (`pruefzyklen.json`).
 */
export const AnlagenTyp = z.enum([
    'feuerloescher_tragbar',
    'feuerloescher_fahrbar',
    'wandhydrant',
    'brandmeldeanlage',
    'rauchwarnmelder',
    'rwa',
    'feststellanlage',
    'feuerschutzabschluss',
    'brandschutzklappe',
    'sicherheitsbeleuchtung',
    'sprinkleranlage',
    'brandschutzabschottung',
    'sicherheitskennzeichnung',
]);
export const ANLAGEN_TYP_LABEL = {
    feuerloescher_tragbar: 'Feuerlöscher (tragbar)',
    feuerloescher_fahrbar: 'Feuerlöscher (fahrbar)',
    wandhydrant: 'Wandhydrant',
    brandmeldeanlage: 'Brandmeldeanlage',
    rauchwarnmelder: 'Rauchwarnmelder',
    rwa: 'Rauch- und Wärmeabzugsanlage',
    feststellanlage: 'Feststellanlage',
    feuerschutzabschluss: 'Feuerschutzabschluss (Tür, Tor)',
    brandschutzklappe: 'Brandschutzklappe',
    sicherheitsbeleuchtung: 'Sicherheitsbeleuchtung',
    sprinkleranlage: 'Sprinkler- und Löschanlage',
    brandschutzabschottung: 'Brandschutzabschottung',
    sicherheitskennzeichnung: 'Sicherheits- und Rettungszeichen',
};
/**
 * Wer die Prüfung durchführen darf.
 *
 * Fachlich bedeutsam, nicht dekorativ: Eine monatliche Erprobung durch den
 * Betreiber ersetzt keine jährliche Wartung durch die Fachkraft. Wer das
 * zusammenwirft, hält eine Anlage für geprüft, die es nicht ist.
 */
export const Durchfuehrender = z.enum(['betreiber', 'fachkraft', 'sachverstaendiger']);
export const DURCHFUEHRENDER_LABEL = {
    betreiber: 'Betreiber',
    fachkraft: 'Fachkraft',
    sachverstaendiger: 'Sachverständiger',
};
/** Lebenszustand einer Anlage. Nur `aktiv` erzeugt Prüffristen. */
export const AnlagenStatus = z.enum(['aktiv', 'ausser_betrieb', 'ausgebaut']);
export const ANLAGEN_STATUS_LABEL = {
    aktiv: 'In Betrieb',
    ausser_betrieb: 'Außer Betrieb',
    ausgebaut: 'Ausgebaut',
};
/**
 * Ergebnis einer durchgeführten Prüfung.
 *
 * `nicht_pruefbar` ist bewusst eigenständig: Eine Anlage, die verstellt oder
 * unzugänglich war, ist weder in Ordnung noch mangelhaft — sie ist ungeprüft,
 * und genau das muss im Protokoll stehen.
 */
export const PruefErgebnis = z.enum(['ok', 'mangel', 'nicht_pruefbar']);
export const PRUEF_ERGEBNIS_LABEL = {
    ok: 'Ohne Beanstandung',
    mangel: 'Mangel festgestellt',
    nicht_pruefbar: 'Nicht prüfbar',
};
/**
 * Schweregrad eines Mangels.
 *
 * Bewusst an der HANDLUNGSFOLGE ausgerichtet, nicht an einer Zahlenskala:
 * Der Planer muss aus dem Wort ablesen können, wie schnell etwas passieren
 * muss. „Mittel" sagt das nicht, „bis zur nächsten Begehung" schon.
 */
export const MangelSchwere = z.enum(['gefahr', 'erheblich', 'geringfuegig', 'empfehlung']);
export const MANGEL_SCHWERE_LABEL = {
    gefahr: 'Gefahr im Verzug',
    erheblich: 'Erheblich',
    geringfuegig: 'Geringfügig',
    empfehlung: 'Empfehlung',
};
export const MANGEL_SCHWERE_HINWEIS = {
    gefahr: 'Sofort zu beheben; der Betreiber ist unverzüglich zu unterrichten.',
    erheblich: 'Innerhalb einer gesetzten Frist zu beheben.',
    geringfuegig: 'Bis zur nächsten Begehung zu beheben.',
    empfehlung: 'Keine Pflicht — ein Hinweis zur Verbesserung.',
};
/**
 * Lebenszyklus eines Mangels.
 *
 * `freigemeldet` und `behoben` sind bewusst getrennt: Der Betreiber MELDET,
 * dass er etwas erledigt hat — bestätigt hat das noch niemand. Erst die
 * Nachschau durch den Betreuer schließt den Mangel. Diese Trennung ist der
 * ganze Grund, warum die Freimeldung existiert: Sie spart den Kontrollgang
 * nicht, sie terminiert ihn.
 */
export const MangelStatus = z.enum([
    'offen',
    'in_bearbeitung',
    'freigemeldet',
    'behoben',
    'hinfaellig',
]);
export const MANGEL_STATUS_LABEL = {
    offen: 'Offen',
    in_bearbeitung: 'In Bearbeitung',
    freigemeldet: 'Freigemeldet — Nachschau offen',
    behoben: 'Behoben',
    hinfaellig: 'Hinfällig',
};
/** Woher ein Mangel stammt. Bestimmt, was beim Abschluss nachgehalten wird. */
export const MangelQuelle = z.enum(['begehung', 'pruefung', 'meldung', 'sonstiges']);
export const MANGEL_QUELLE_LABEL = {
    begehung: 'Brandschutzbegehung',
    pruefung: 'Prüfung einer Anlage',
    meldung: 'Meldung des Betreibers',
    sonstiges: 'Sonstiges',
};
/**
 * Stand einer Begehung.
 *
 * `abgeschlossen` ist eine Einbahnstraße: Danach ist das Protokoll
 * schreibgeschützt — dieselbe Linie wie beim abgeschlossenen Projekt. Ein
 * Bericht, der nachträglich änderbar bleibt, taugt nicht als Nachweis.
 */
export const BegehungStatus = z.enum(['geplant', 'laufend', 'abgeschlossen']);
export const BEGEHUNG_STATUS_LABEL = {
    geplant: 'Geplant',
    laufend: 'Läuft',
    abgeschlossen: 'Abgeschlossen',
};
/**
 * Art der Betreuung einer Liegenschaft.
 *
 * Die Unterscheidung entscheidet, ob eine Frist verbindlich oder nur
 * empfohlen ist: Wer als Brandschutzbeauftragter BESTELLT ist, übernimmt die
 * Aufgaben aus DGUV Information 205-003 als Pflicht — der Berater schlägt sie
 * vor. Ohne diesen Ort im Modell könnte keine Aufgabe als verbindlich gelten.
 */
export const BetreuungArt = z.enum(['bsb_bestellung', 'beratung', 'wartung']);
export const BETREUUNG_ART_LABEL = {
    bsb_bestellung: 'Bestellung als Brandschutzbeauftragter',
    beratung: 'Beratung',
    wartung: 'Wartung',
};
/** Wie die Betreuung abgerechnet wird — Vorgriff auf die Rechnungsstellung. */
export const Abrechnungsmodell = z.enum(['abo', 'einzel']);
export const ABRECHNUNGSMODELL_LABEL = {
    abo: 'Pauschale (Abo)',
    einzel: 'Einzelabrechnung',
};
/** Wer eine wiederkehrende Aufgabe erledigt. */
export const AufgabeZustaendig = z.enum(['buero', 'kunde', 'extern']);
export const AUFGABE_ZUSTAENDIG_LABEL = {
    buero: 'Büro',
    kunde: 'Kunde',
    extern: 'Externer Dienstleister',
};
/** Ob eine Aufgabe im Betreuungspaket enthalten ist oder gesondert berechnet wird. */
export const AufgabeAbrechnung = z.enum(['im_paket', 'separat']);
export const AUFGABE_ABRECHNUNG_LABEL = {
    im_paket: 'Im Paket',
    separat: 'Separat',
};
/**
 * Rang der Rechtsgrundlage eines Katalogeintrags.
 *
 * Gesetz und Unfallverhütungsvorschrift binden unmittelbar, eine ASR entfaltet
 * Vermutungswirkung (§ 3a ArbStättV), eine DIN bindet nur bei bauaufsichtlicher
 * Einführung, Zulassung oder Vertrag. „Empfehlung" ist alles, was aus einer
 * DGUV Information, einem VdS-Blatt oder der Fachliteratur stammt — und alles,
 * dessen Zahl sich nicht belegen ließ.
 */
export const Verbindlichkeit = z.enum(['gesetz', 'uvv', 'asr', 'din', 'empfehlung']);
export const VERBINDLICHKEIT_LABEL = {
    gesetz: 'Gesetz / Verordnung',
    uvv: 'DGUV Vorschrift',
    asr: 'Technische Regel (ASR)',
    din: 'DIN-Norm',
    empfehlung: 'Empfehlung',
};
/**
 * Dringlichkeit einer Frist — gemeinsam für Anlagen-Prüfzyklen und Aufgaben.
 *
 * `ohne_frist` ist ein echter Zustand und kein „in Ordnung": Eine Frist, die
 * sich nicht berechnen lässt, ist unterminiert, nicht erledigt. Wo die Grenze
 * zu `faellig_bald` liegt, sagt der Server (config-driven), nicht die
 * Oberfläche.
 */
export const FaelligkeitStatus = z.enum(['ueberfaellig', 'faellig_bald', 'ok', 'ohne_frist']);
export const FAELLIGKEIT_STATUS_LABEL = {
    ueberfaellig: 'Überfällig',
    faellig_bald: 'Bald fällig',
    ok: 'In Ordnung',
    ohne_frist: 'Ohne Frist',
};
/**
 * Verbindlichkeit der Frist an einem Mangel.
 *
 * Aus den Prozessbildern des Auftraggebers: Ist der Betrieb als
 * Brandschutzbeauftragter bestellt, ist die Frist gegenüber dem Kunden
 * verbindlich; bei reiner Beratung ist sie eine Empfehlung. Der Bericht muss
 * beides auseinanderhalten — sonst liest der Kunde eine Empfehlung als Auflage
 * oder eine Auflage als Hinweis.
 */
export const FristArt = z.enum(['verbindlich', 'empfohlen']);
export const FRIST_ART_LABEL = {
    verbindlich: 'Verbindlich',
    empfohlen: 'Empfohlen',
};
/**
 * Woher ein Eintrag der Textbibliothek stammt.
 *
 * `aus_begehung` ist der Zweig „kein Bibliothekstreffer, neuer Text wird
 * abgelegt": Der Mangel wurde vor Ort formuliert und danach mit einem Klick
 * zur Vorlage. `manuell` ist die Pflege in der Bibliothek selbst.
 */
export const MangelVorlageQuelle = z.enum(['manuell', 'aus_begehung']);
export const MANGEL_VORLAGE_QUELLE_LABEL = {
    manuell: 'In der Bibliothek angelegt',
    aus_begehung: 'Aus einer Begehung übernommen',
};
/**
 * Stand eines Eintrags in der Übernahme-Checkliste.
 *
 * `offen` ist der Anfangszustand: noch niemand hat nachgesehen. `fehlt` und
 * `nicht_zutreffend` sind bewusst getrennt — das eine ist eine Lücke, die
 * eine Bewertung braucht, das andere eine Entscheidung, dass die Unterlage in
 * diesem Objekt nicht erwartet wird. `angefordert` heißt: beim Auftraggeber
 * nachgefragt, Antwort steht aus.
 */
export const UebernahmeStatus = z.enum([
    'offen',
    'vorhanden',
    'fehlt',
    'angefordert',
    'nicht_zutreffend',
]);
export const UEBERNAHME_STATUS_LABEL = {
    offen: 'Offen',
    vorhanden: 'Vorhanden',
    fehlt: 'Fehlt',
    angefordert: 'Angefordert',
    nicht_zutreffend: 'Nicht zutreffend',
};
